import React from 'react';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

// Create a dark theme that won't be affected by global styles
const createDarkTheme = () => {
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#4dabf5',
      },
      secondary: {
        main: '#64b5f6',
      },
      background: {
        default: '#121212',
        paper: '#1e1e1e',
      },
      text: {
        primary: '#ffffff',
        secondary: '#b3b3b3'
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            textTransform: 'none',
            boxShadow: 'none',
            fontFamily: '"Roboto Mono", "Courier New", monospace',
            '&.MuiButton-containedPrimary': {
              backgroundColor: '#4dabf5',
              color: '#ffffff',
              border: 'none',
            },
            '&.MuiButton-outlinedSecondary': {
              borderColor: '#64b5f6',
              color: '#64b5f6',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            minWidth: 0,
            fontFamily: '"Roboto Mono", "Courier New", monospace',
            '&.Mui-selected': {
              color: '#4dabf5',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: '#4dabf5',
            height: '2px',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: 'none',
            fontFamily: '"Roboto Mono", "Courier New", monospace',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderWidth: '1px',
                borderColor: 'rgba(255, 255, 255, 0.1)',
              },
            },
            '& .MuiInputLabel-root': {
              fontFamily: '"Roboto Mono", "Courier New", monospace',
            },
            '& .MuiInputBase-root': {
              fontFamily: '"Roboto Mono", "Courier New", monospace',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            fontFamily: '"Roboto Mono", "Courier New", monospace',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: '"Roboto Mono", "Courier New", monospace',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: '#1e1e1e',
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            backgroundColor: '#1e1e1e',
          }
        }
      },
      MuiCssBaseline: {
        styleOverrides: `
          .isolate-styles button {
            font-family: "Roboto Mono", "Courier New", monospace;
            border: none;
            background-image: none;
            box-shadow: none;
          }
          
          .isolate-styles [class*="bg-"] {
            background-color: inherit;
          }
          
          .isolate-styles [class*="text-"] {
            color: inherit;
          }
          
          .isolate-styles [class*="border-"] {
            border-color: inherit;
          }
          
          .isolate-styles * {
            font-family: "Roboto Mono", "Courier New", monospace;
          }
          
          .isolate-styles div, .isolate-styles section, .isolate-styles article {
            border: none;
          }
        `,
      },
    },
    typography: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      h4: {
        fontFamily: '"Roboto Mono", "Courier New", monospace',
      },
      h5: {
        fontFamily: '"Roboto Mono", "Courier New", monospace',
      },
      h6: {
        fontFamily: '"Roboto Mono", "Courier New", monospace',
      },
      body1: {
        fontFamily: '"Roboto Mono", "Courier New", monospace',
      },
      body2: {
        fontFamily: '"Roboto Mono", "Courier New", monospace',
      },
    },
  });
};

// Theme provider component with styling isolation
const DarkTheme = ({ children }) => {
  const darkTheme = createDarkTheme();
  
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={darkTheme}>
        <div 
          className="isolate-styles" 
          style={{ 
            // Forcefully reset any inherited styles
            all: 'initial',
            fontFamily: '"Roboto Mono", "Courier New", monospace',
            color: '#ffffff',
            backgroundColor: '#121212',
            minHeight: '100vh',
            width: '100%',
            boxSizing: 'border-box',
            display: 'block',
          }}
        >
          {children}
        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default DarkTheme;
